import { type FC, useState } from "react";
import {
  Flex,
  Form,
  FormRow,
  IconButton,
  Message,
  SubmitButton,
  Text,
  TextField,
  type FormProps,
} from "@huub-web/ui";
import { useTranslation } from "react-i18next";

import { useAuthConfig } from "../../../hooks";
import type { BaseSlideProps } from "../../../types";

import { slideContainerStyle } from "../AuthScreens.css";
import { formStyle } from "./LoginSlide.css";

export type ForgotPasswordSlideProps = BaseSlideProps & {
  onBackToLogin?: () => void;
  onLoadingChange?: (loading: boolean) => void;
};

/**
 * ForgotPasswordSlide component for password reset.
 * Allows users to request a password reset email via Auth0.
 */
export const ForgotPasswordSlide: FC<ForgotPasswordSlideProps> = ({
  leading,
  trailing,
  onBackToLogin,
  onLoadingChange,
}) => {
  const { t } = useTranslation();
  const config = useAuthConfig();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit: FormProps["onSubmit"] = (_e, valid) => {
    if (!valid) {
      return;
    }

    const submitAsync = async () => {
      setIsSubmitting(true);
      onLoadingChange?.(true);
      setError(null);

      try {
        const response = await fetch(
          `https://${config.auth0.domain}/dbconnections/change_password`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              client_id: config.auth0.clientId,
              email,
              connection: "Username-Password-Authentication",
            }),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to send password reset email");
        }

        setIsEmailSent(true);
      } catch (err) {
        console.error("[ForgotPassword] Error:", err);
        setError(t("authScreens.forgotPassword.errorMessage"));
      } finally {
        setIsSubmitting(false);
        onLoadingChange?.(false);
      }
    };

    void submitAsync();
  };

  const handleBackToLogin = () => {
    setEmail("");
    setIsEmailSent(false);
    setError(null);
    onBackToLogin?.();
  };

  return (
    <Flex
      className={slideContainerStyle}
      direction="column"
      gap="m"
      align="center"
      justify="center"
    >
      {onBackToLogin && (
        <IconButton
          icon="chevronLeft"
          onClick={handleBackToLogin}
          alignSelf="flex-start"
          rounded
          size="xs"
          border="default"
          variant="opaque"
        />
      )}

      {!onBackToLogin && leading}

      <Flex direction="column" gap="s" align="center">
        <Text as="h2" variant="displayM" bold align="center">
          {t("authScreens.forgotPassword.title")}
        </Text>
        <Text as="p" align="center">
          {t("authScreens.forgotPassword.description")}
        </Text>
      </Flex>

      {error && <Message variant="error" body={error} />}
      {isEmailSent && (
        <Message
          variant="success"
          body={t("authScreens.forgotPassword.successMessage")}
        />
      )}

      <Form
        disabled={isSubmitting}
        onSubmit={handleSubmit}
        className={formStyle}
      >
        <FormRow id="email">
          <TextField
            name="email"
            label={t("registration.emailLabel")}
            value={email}
            onChange={setEmail}
            validation={["required", "email"]}
            disabled={isEmailSent}
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
          />
        </FormRow>

        <SubmitButton
          variant="primary"
          size="s"
          align="center"
          disabled={isEmailSent}
        >
          {t("authScreens.forgotPassword.submitButton")}
        </SubmitButton>
      </Form>

      {trailing}
    </Flex>
  );
};
